import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  addQueryParamWithoutRedirecting,
  assemble,
  createFiltersFromRedirect,
  formatDateLang,
  formatFilters,
  isItUndefined,
  pushSearchSuggestionClickEvent,
} from '../../util/DataUtil';
import ResourceFilter from '../resources/resource-filter';
import { getLanguage, useWindowSize } from '../../util/PageUtil';
import { useRouter } from 'next/router';
import { getBackendUrl } from '../../util/Constants';
import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { setLocalStorageRedux } from '../../store/localStorageSlice';
import Select from 'react-select';
import ArticleThumbnailCard from '../shared/ArticleThumbnailCard';
import { SearchBarPlacement } from '../../constants/SearchBarPlacement';
import { FilterName } from '../../constants/FilterName';
import PaginationLink from '../shared/PaginationLink';
import {
  buildPageHref,
  getPageFromQuery,
  buildSearchHref,
  getSearchFromQuery,
  hasNextPage,
  stripListingParams,
} from '../../util/PaginationUtil';

// Must match LATEST_ARTICLES_PER_PAGE in util/PageUtil.js — the server renders
// that many per page, and the seeding below slices on the same boundary.
const ARTICLES_PER_PAGE = 9;

const LatestArticles = ({
  lang,
  global,
  topics,
  data,
  contentType,
  nodeId,
  contentTags,
  initialArticles,
  // Whether the server found articles beyond the page it rendered.
  initialArticlesHasMore,
}) => {
  const router = useRouter();
  const windowSize = useWindowSize();
  // Seeded from the URL so `?page=3` lands on page three instead of silently
  // showing page one. `router.query` is already populated during SSR for this
  // route, so the server and the first client render agree.
  const serverPage = getPageFromQuery(router?.query);
  const serverArticles = Array.isArray(initialArticles) ? initialArticles : [];

  // Seeded from the URL so a reload or a shared link restores the same search,
  // and so pagination composes with it (`?search=cloud&page=2`).
  const [query, setQuery] = useState(() => {
    const term = getSearchFromQuery(router?.query);
    return term ? [term] : [];
  });
  const [page, setPage] = useState(serverPage);
  const [facetFilters, setFacetFilters] = useState([]);
  const [languageFilter, setLanguageFilter] = useState([]);
  const [results, setResults] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  const [filterProps, setFilterProps] = useState([]);
  const [showMore, setShowMore] = useState(serverArticles);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [closeFilters, setCloseFilters] = useState(false);
  // On an author page the list is that author's from the very first request.
  // It used to be set by the effect below, after its skip for a deep page —
  // so a reloaded `?page=2` never got it, and See More fetched the general
  // Blog pool.
  const [author, setAuthor] = useState(
    contentType === 'author' && nodeId ? nodeId : ''
  );
  // Whether a page exists after the last one loaded. Known for certain —
  // each request asks for one article more than it shows — so the See More
  // link, and the rel="next" it carries, stop on the last page even when
  // that page is exactly full.
  const [hasMore, setHasMore] = useState(() =>
    typeof initialArticlesHasMore === 'boolean'
      ? initialArticlesHasMore
      : hasNextPage({
          page: serverPage,
          lastPageSize: serverArticles.length,
          perPage: ARTICLES_PER_PAGE,
        })
  );
  const [topicsAndAuthors, setTopicsAndAuthors] = useState([]);
  // `currentPageData` means "the last batch loaded". Whether See More shows is
  // `hasMore`, above, not whether this batch was full.
  //
  // The server sends the requested page and nothing else, so the whole response
  // is that batch.
  const [currentPageData, setCurrentPageData] = useState(serverArticles);
  // const [contentTagSelection, setContentTagSelection] = useState([]);
  const [authors, setAuthors] = useState([]);
  const controller = useRef(null);

  // A deep page arrives already rendered from the server. Letting the mount
  // effect refetch it would show the same nine articles twice, because for
  // `page > 0` fetchArticlesData concatenates onto `showMore` instead of
  // replacing it. Page one is safe to refetch — that branch replaces, and the
  // refetch is what pulls in the manually added articles.
  // Server-rendered results are always unfiltered, so when the URL carries a
  // filter or a search term the client has to fetch again — skipping that left
  // the filter showing as selected while the list underneath stayed unfiltered.
  const urlNarrowsResults =
    Object.keys(stripListingParams(router?.query) || {}).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const skipInitialFetch = useRef(
    serverPage > 0 && serverArticles.length > 0 && !urlNarrowsResults
  );

  // The deepest page fetched so far, so a later fetch can tell "Show More was
  // clicked" from "something else changed and this list needs rebuilding".
  // Seeded from the URL: the server already rendered that page.
  const lastFetchedPage = useRef(serverPage);
  const authorsTopicId =
    topics?.find((t) => t.field_source_translation === 'Authors')?.id || '0';

  useEffect(() => {
    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, []);

  const manuallyAddedArticles = data.field_manually_added_articles;

  const myLocalStorage = useSelector(setLocalStorageRedux);

  const isAuthorContentType = contentType === 'author';

  const hitsPerPage = ARTICLES_PER_PAGE;

  let lng = lang;

  if (typeof window !== 'undefined') {
    var backendUrl = getBackendUrl(window.location.host)?.remoteServer;
  }

  const sortByOptions = [
    {
      value: 'recent',
      label: data.field_most_recent_text?.[0]?.value || 'Recent',
    },
    {
      value: 'popular',
      label: data.field_most_popular_text?.[0]?.value || 'Popular',
    },
    { value: 'az', label: data.field_a_z_text?.[0]?.value || 'A-Z' },
    { value: 'za', label: data.field_z_a_text?.[0]?.value || 'Z-A' },
  ];
  const [sortBySelectedOption, setSortBySelectedOption] = useState('recent');

  useEffect(() => {
    if (!lng) {
      return;
    }

    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }

    setIsLoading(true);

    if (isAuthorContentType) {
      setAuthor(nodeId);
    }
    fetchArticlesData();
    // }, [applyFilter, query, sortBySelectedOption, page, author, filterProps]);
  }, [applyFilter, sortBySelectedOption, page, author, filterProps]);

  useEffect(() => {
    fetchAuthorsData();
  }, []);

  const triggerFilterFromRedirect = (routeFilter) => {
    // let filters = createFiltersFromRedirect(filter, topicsAndAuthors);
    const { slug, ...cleanFilter } = routeFilter;

    const topicFilters = createFiltersFromRedirect(cleanFilter, topics);
    const tagFilters = createFiltersFromRedirect(cleanFilter, contentTags);
    tagFilters.forEach((tag) => {
      tag.tid = tag.id;
    });
    const authorFilters = createFiltersFromRedirect(cleanFilter, authors);

    const filters = [...topicFilters, ...tagFilters, ...authorFilters];

    triggerFilter(filters, false);
    setApplyFilter(true);
  };

  // `resetPage` is false when the filters are being hydrated from the URL
  // rather than chosen by the visitor. Someone opening a shared
  // `?type=blueprints&search=te&page=2` has not changed anything — treating
  // that as a filter change reset the page to one and stripped it from the
  // address before they saw it.
  const triggerFilter = (filters, resetPage = true) => {
    setFilterProps(filters);

    let searchFilters = [];
    for (let filter of filters) {
      searchFilters.push(filter?.id);
    }

    setFacetFilters(searchFilters);
    setApplyFilter(false);

    // Back to the first page: the filtered set is a different, shorter list, so
    // staying on page three would ask for a slice of it that does not exist and
    // the results would come back empty.
    //
    // Skipped when `resetPage` is false — that is the URL being hydrated on
    // arrival, where the page in the address has to survive.
    if (resetPage) {
      setPage(0);
    }

    // The address itself is deliberately not touched here. The filter control
    // writes the URL and drops the page while doing it, and `router.asPath` is
    // still the pre-filter value at this point — rewriting from it rewound the
    // address and threw the filter straight back out.
  };

  // Hydrating the filters out of the URL is a one-time thing. This effect
  // re-runs whenever its dependencies settle — the author list arriving, for
  // instance — and on a later run it would read the URL again and re-apply it
  // on top of whatever the visitor had since selected, overwriting their choice
  // with a fresh mapping. That looked like the filter being applied and then
  // quietly ignored.
  const hydratedFromUrl = useRef(false);

  useEffect(() => {
    // `page` is in the query string but is not a filter. Letting it through
    // makes the check below think the visitor arrived with filters applied,
    // which resets the page to one — so reloading `?page=2` showed page one.
    let localFilter = stripListingParams(router.query);

    if (typeof window !== 'undefined') {
      // The URL still carries filters, so there is nothing to clear — whether
      // or not this run is the one that hydrated them.
      //
      // The clear branch used to be reachable on the second run: the first
      // applied `?category=ai` and set the guard, then the author list arrived,
      // the effect re-ran, the guard sent it past the hydrate branch and
      // straight into `clearAll()`. The articles endpoint was asked for
      // `topics=1448` and then immediately for `topics=`, so a reload of a
      // filtered URL showed the filter neither selected nor applied.
      if (hasNonEmptyProperties(localFilter)) {
        if (!hydratedFromUrl.current) {
          hydratedFromUrl.current = true;
          triggerFilterFromRedirect(localFilter);
        }
      } else if (filterProps.length > 0 || query.length > 0) {
        // Only clear when something is actually applied.
        //
        // `clearAll` resets the page through `triggerFilter([])`, and this
        // effect re-runs whenever its dependencies settle — the author list
        // arriving, for instance. Guarding only the first run was not enough:
        // the second run wiped the page the URL had asked for, so after a
        // reload of `?page=3` the See More link pointed back at page two.
        //
        // With nothing applied there is nothing to clear, so the page the
        // visitor asked for survives.
        clearAll();
      }

    }
  }, [myLocalStorage, authors]);

  function hasNonEmptyProperties(obj) {
    for (const key in obj) {
      if (
        !key.startsWith('slug') &&
        obj[key] !== null &&
        obj[key] !== undefined &&
        obj[key] !== ''
      ) {
        return true;
      }
    }
    return false;
  }
  const fetchAuthorsData = () => {
    let url =
      backendUrl +
      'authors?' +
      'langcode=' +
      lng +
      '&filter=az&from=0&count=100';

    const fetchAuthors = async () => {
      try {
        const response = await fetch(url);
        const authorsResponse = await response.json();

        const filteredAuthors = authorsResponse
          .filter(
            (author) => author.field_exclude_from_filter?.[0]?.value !== '1'
          )
          .map((author, i) => ({
            id: author.node_id,
            name: author.field_author_full_name?.[0]?.value,
            parent_id: authorsTopicId,
            description: 'Author',
            weight: i + 1,
            field_source_translation: author.field_author_full_name?.[0]?.value,
          }));

        const updatedTopics = [...topics, ...filteredAuthors];

        setTopicsAndAuthors(updatedTopics);
        setAuthors(filteredAuthors);
      } catch (error) {
        console.error('Error fetching Authors data:', error);
      }
    };
    fetchAuthors();
  };

  // This effect exists to react to the search box changing. On mount there is
  // nothing to react to — the effect above already fetches — and letting it run
  // there did real damage: with no term, `query?.[0]?.length < 3` is
  // `undefined < 3`, which is false, so it scheduled a fetch anyway. That fetch
  // was closed over the render that scheduled it, 300ms before the URL's
  // filters were hydrated, so it landed last and overwrote the filtered results
  // with unfiltered ones.
  const skipQueryEffect = useRef(true);

  useEffect(() => {
    if (skipQueryEffect.current) {
      skipQueryEffect.current = false;
      return;
    }

    if (query === undefined || query?.[0]?.length < 3) return;
    const delayDebounce = setTimeout(() => {
      fetchArticlesData();
      setIsLoading(true);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query]);
  // }, [query, facetFilters, languageFilter, sortBySelectedOption, page]);

  const fetchArticlesData = () => {
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = new AbortController();
    setResults(
      (query.length || facetFilters.length || languageFilter.length) !== 0
    );

    let hits = hitsPerPage;
    if (page == 0 && facetFilters.length == 0) {
      hits = hitsPerPage - manuallyAddedArticles.length;
    }

    const topicIds = filterProps
      .filter((item) => item.description !== 'Author' && !item.tid) //tid is tag id
      .map((item) => item.id);
    const topicIdsString = topicIds.join(',');

    const currentTagIds = filterProps.map((filter) => filter.tid);

    // const tagIdsString = currentTagIds.join(',');
    const tagIdsString = currentTagIds
      .filter((id) => id != null && id !== undefined && id !== '')
      .join(',');

    const authorIds = filterProps
      .filter((item) => item.description === 'Author')
      .map((item) => item.id);

    let authorIdsString = '';
    if (author) {
      authorIdsString = author;
    } else {
      authorIdsString = authorIds.join(',');
    }

    let url =
      backendUrl +
      'articles?' +
      'langcode=' +
      lng +
      '&filter=' +
      sortBySelectedOption +
      '&from=' +
      page * hits +
      '&count=' +
      // One extra, to tell whether another page follows.
      (hits + 1) +
      '&topics=' +
      topicIdsString +
      '&author=' +
      authorIdsString +
      '&search=' +
      query +
      '&tags=' +
      tagIdsString;

    const fetchArticles = async () => {
      try {
        const response = await fetch(url, {
          signal: controller.current.signal,
        });
        const fetched = await response.json();
        // One more than a page was asked for; the extra only says whether a
        // next page exists and is not shown.
        const newResponse = Array.isArray(fetched)
          ? fetched.slice(0, hits)
          : fetched;

        if (Array.isArray(fetched)) {
          setHasMore(fetched.length > hits);
        }

        // Append only when this fetch is a step forward in the series —
        // i.e. Show More was clicked. Every other reason to refetch (a
        // filter, a sort, a new search) replaces the list.
        //
        // The test used to be `page == 0`, which is not the same thing: this
        // effect also re-runs on a deep page when something else in its
        // dependencies settles after hydration, and that run appended the
        // page on top of the copy the server had already rendered. `?page=3`
        // showed eighteen articles — page three, twice.
        const isLoadMore = page > lastFetchedPage.current;
        lastFetchedPage.current = page;

        if (isLoadMore) {
          setShowMore([...showMore, ...newResponse]);
          setCurrentPageData(newResponse);
        } else if (page === 0) {
          //TODO: manuallyAddedArticles
          setShowMore([...manuallyAddedArticles, ...newResponse]);
          setCurrentPageData([...manuallyAddedArticles, ...newResponse]);
        } else {
          setShowMore(newResponse);
          setCurrentPageData(newResponse);
        }

        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      } catch (error) {
        console.error('Error fetching Articles data:', error);
      }
    };
    fetchArticles();
  };

  const handleSearch = (search) => {
    setQuery(search.length !== 0 ? [search] : []);
    setPage(0);
    setApplyFilter(false);

    // Put the term in the address bar. Without it, clicking See More produced a
    // bare `?page=2` — a URL that on reload showed page two of the *unfiltered*
    // list while the search box sat empty.
    //
    // `replace` rather than `push` so typing does not leave one history entry
    // per keystroke; `buildSearchHref` drops the page, because a new search
    // starts at the beginning.
    // Only when the term actually changes what is in the URL.
    //
    // This is handed to the filter component as `onSearchChange`, so it fires
    // while a filter is being applied — with an empty term and a `router.asPath`
    // that has not caught up with the filter's own push yet. Writing then
    // rewound the address to its pre-filter state and wiped the filter out.
    if (getSearchFromQuery(router.query) !== search) {
      router.replace(buildSearchHref(router.asPath, search), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  const clearAll = () => {
    setFilterProps([]);
    triggerFilter([]);
    setLanguageFilter([]);
    if (query.length > 0) {
      setQuery([]);
    }
    setShowMore([]);
    setCloseFilters(false);
  };

  const clearQuery = () => {
    setQuery([]);
    // Only when a term is actually in the URL. This is handed to the filter
    // component as a prop and fires while a filter is being applied — rewriting
    // the address unconditionally raced with the filter's own push and wiped
    // the filter straight back out of it.
    if (getSearchFromQuery(router.query)) {
      router.replace(buildSearchHref(router.asPath, ''), undefined, {
        shallow: true,
        scroll: false,
      });
    }
    setIsSearchActive(false);
    setIsFilterActive(true);
  };

  const renderBlogInfo = (blogItem) => {
    // const authors = blogItem?.field_authors || [];
    // const authorNames =
    //   authors
    //     ?.map((a) => a?.field_author_full_name?.[0]?.value)
    //     .filter(Boolean)
    //     .join(', ') || null;

    const authorBlocks = Array.isArray(blogItem?.field_template_blocks)
      ? blogItem.field_template_blocks.filter(
          (block) => block?.author_block !== undefined
        )
      : [];

    const authorNames =
      authorBlocks
        ?.map((a) => a?.author_block?.field_author_name?.[0]?.value)
        .filter(Boolean)
        .join(', ') || null;

    const publishDate = isItUndefined(blogItem?.field_publish_date) || null;
    const timeToRead = isItUndefined(blogItem?.field_time_to_read) || null;
    return (
      <div className={'blog-info'}>
        <span className={'author-name'}>
          {authorNames ? `${authorNames}` : ''}
        </span>

        {authorBlocks.length > 1 && <br />}
        {authorBlocks.length === 1 && publishDate ? (
          <span className={'separator'}>{' | '}</span>
        ) : null}
        <span className={'publish-date'}>
          {publishDate ? formatDateLang(publishDate) : ''}
        </span>
        {(authorNames || publishDate) && timeToRead ? (
          <span className={'separator'}>{' | '}</span>
        ) : null}
        <span className={'blog-read-time'}>{timeToRead ? timeToRead : ''}</span>
      </div>
    );
  };

  const handleChipClick = (event, filter) => {
    event.preventDefault();
    event.stopPropagation();

    if (isAuthorContentType) {
      router.push({
        pathname: '/resources/blog',
        query: { contentTag: filter.field_source_translation.toLowerCase() },
        hash: 'articles-cards-container',
      });
      // } else if (!contentTagSelection.includes(filter.tid)) {
    } else if (!filterProps.some((item) => item.tid === filter.tid)) {
      setFilterProps([...filterProps, filter]);

      // Drop the page from the path as well as from state. Applying a filter
      // starts a new, shorter result set — keeping `?page=5` would ask for a
      // page of filtered results that very likely does not exist, and would
      // survive a reload in that broken state.
      const [pathWithoutHash] = buildPageHref(router.asPath, 0).split('#');
      const hashToAdd = 'articles-cards-container';

      setPage(0);

      // router.push({
      //   pathname: router.asPath.split('#')[0],
      //   // query: { ...router.query, contentTag: filter.name.toLowerCase() },
      //   hash: 'articles-cards-container',
      // }, undefined, { shallow: true });
      // // setContentTagSelection([...contentTagSelection, filter.tid]);
      // setPage(0);

      router.push(
        `${pathWithoutHash}#${hashToAdd}`, // FULL URL
        undefined,
        { shallow: true }
      );
    }
  };

  const getTitle = (item) => {
    if (item?.field_display_title?.[0]?.value) {
      return item?.field_display_title?.[0]?.value;
    }

    const templateBlocks = item.field_template_blocks;
    let titleBlock = null;

    if (Array.isArray(templateBlocks)) {
      titleBlock =
        templateBlocks.find((block) => block.template_title_block_type)
          ?.template_title_block_type || null;
    } else if (templateBlocks?.template_title_block_type) {
      titleBlock = templateBlocks?.template_title_block_type;
    }
    if (titleBlock?.field_template_title?.[0]?.value) {
      return titleBlock.field_template_title?.[0]?.value;
    }
    return item?.title;
  };

  const getBannerImageSrc = (item) => {
    const templateBlocks = item.field_template_blocks;
    let bannerBlock = null;

    if (Array.isArray(templateBlocks)) {
      bannerBlock =
        templateBlocks.find((block) => block.template_banner_block_type)
          ?.template_banner_block_type || null;
    }

    if (templateBlocks?.template_banner_block_type) {
      bannerBlock = templateBlocks?.template_banner_block_type;
    }

    return (
      bannerBlock?.field_web_small_image?.webimage ??
      bannerBlock?.field_template_banner_bynder?.webimage ??
      ''
    );
  };

  const renderSearchCards = (data) => {
    if (!data) {
      return;
    }

    return data.map((item, index) => (
      <ArticleThumbnailCard
        key={item?.objectID || item?.['url-alias'] || index}
        href={item?.['url-alias']?.length ? item['url-alias'] : '/'}
        image={getBannerImageSrc(item)}
        imageAlt="search-image"
        imagePriority
        header={getTitle(item)}
        subheader={renderBlogInfo(item)}
        primaryCategory={item?.field_template_category?.[0]}
        secondaryCategories={item?.field_template_secondary_categor}
        onTagClick={handleChipClick}
        onClick={() =>
          pushSearchSuggestionClickEvent(
            Array.isArray(query)
              ? (query[0]?.toLowerCase() ?? '')
              : (query?.toLowerCase() ?? ''),
            SearchBarPlacement.ARTICLES,
            getTitle(item),
            index + 1
          )
        }
      />
    ));
  };

  function renderSearchResults(data, global) {
    if (!data) {
      return;
    }

    if (data.length === 0) {
      return (
        <div className={'no-result'}>
          <h2>
            {global?.field_no_results_text?.[0]?.value ||
              'No results were found. Please check the spelling, reset any filters and try again.'}
          </h2>
          <div
            className={'clear-all'}
            onClick={() => {
              clearAll();
            }}
          >
            {global?.field_clear_all_label?.[0]?.value ||
              'Clear all search terms.'}
          </div>
        </div>
      );
    }
    return renderSearchCards(showMore);
  }

  const handleSortOptionChange = (option) => {
    setSortBySelectedOption(option.value);
    setPage(0);
    setApplyFilter(true);
  };

  const renderSortByDropDown = () => {
    return (
      <div className="sort-by">
        <label
          htmlFor="select"
          id="label"
          style={{ width: '100px', zIndex: '1' }}
        >
          {data?.field_sort_by_text?.[0]?.value || 'Sort By'}
        </label>
        <Select
          id="select"
          aria-labelledby="label"
          defaultValue={sortByOptions[0]}
          options={sortByOptions}
          onChange={(selectedOption) => handleSortOptionChange(selectedOption)}
        ></Select>
      </div>
    );
  };

  return (
    <div id="articles-cards-container" className="latest-articles">
      {!isAuthorContentType ? (
        <ResourceFilter
          metrosLabel={isItUndefined(global.field_metro_label) || 'Metros'}
          singleFilter={false}
          clearQuery={clearQuery}
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          setIsSearchActive={setIsSearchActive}
          setIsFilterActive={setIsFilterActive}
          search={
            data.field_search_articles_text?.[0]?.value ||
            isItUndefined(global.field_search_text_translation)
          }
          filterProps={filterProps}
          triggerFilter={triggerFilter}
          data={assemble(topicsAndAuthors)}
          onSearchChange={handleSearch}
          handleFilterApply={setApplyFilter}
          searchQuery={query}
          setQuery={setQuery}
          languageFilter={languageFilter}
          setLanguageFilter={setLanguageFilter}
          screenWidth={windowSize.width}
          clearAll={clearAll}
          title={
            data.field_filter_articles_text?.[0]?.value ||
            isItUndefined(global.field_filter_text)
          }
          cancelText={isItUndefined(global.field_cancel_text_translation)}
          applyFilterText={isItUndefined(global.field_apply_filters_text)}
          clearAllText={isItUndefined(global.field_clear_all_text)}
          selectedText={isItUndefined(global.field_selected_text)}
          seeMoreText={isItUndefined(global?.field_see_more_text)}
          seeLessText={isItUndefined(global?.field_see_less)}
          isPartnerPage={false}
          showAdvancedFilterButtons={false}
          alwaysDarkFilter={true}
          placement={SearchBarPlacement.ARTICLES}
          dataLayerFilterName={FilterName.ARTICLES}
        />
      ) : null}

      {/*{!isLoading ? (*/}
      {/*    algoliaData.nbHits != 0 ? (*/}
      <div className={'search-results-header-latest-articles inner-container'}>
        <div className={'results-text'}>
          <div>
            {filterProps.length === 0
              ? data.field_latest_articles_title?.[0]?.value ||
                'Latest Articles'
              : data.field_results_text?.[0]?.value || 'Results'}
          </div>
          {renderSortByDropDown()}
        </div>

        <div className={'horizontal-line'} />
        {isLoading && page == 0 ? (
          <div className={'loader'}>
            <CircularProgress />
          </div>
        ) : (
          <Fragment>
            <div className={'search-results'}>
              {renderSearchResults(showMore, global, data)}
            </div>
            {/*{algoliaData.page + 1 !== algoliaData.nbPages &&*/}
            {/*  algoliaData.nbHits !== 0 && (*/}
            {isLoading ? (
              <div className={'loader'}>
                <CircularProgress />
              </div>
            ) : !hasMore ? null : (
              <PaginationLink page={page + 1} onLoadMore={setPage}>
                {global?.field_see_more_text?.[0]?.value || 'See More'}
              </PaginationLink>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};
export default LatestArticles;
